/*
 * Throwaway preview — shows 3 options for combining Dropship Automation + Terms tabs.
 * No backend calls; pure visual mockup. Hit /preview/agreement-options to view.
 */
import { useState } from "react";
import {
  ShieldCheck, Clock, FileSignature, Settings, Store, UserCheck, Sparkles,
  Building2, ChevronRight, Info, ScrollText,
} from "lucide-react";

export default function AgreementOptionsPreview() {
  const [option, setOption] = useState("A");
  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center gap-6">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal</div>
            <div className="display text-xl mt-0.5">Combining Automation + Terms — 3 options</div>
          </div>
          <div className="ml-auto flex items-center gap-1 rounded-full border border-border bg-white p-1">
            {["A", "B", "C"].map(k => (
              <button key={k}
                onClick={() => setOption(k)}
                className={`rounded-full px-5 h-9 text-[12.5px] mono uppercase tracking-[0.15em] transition-colors ${option === k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                Option {k}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 py-10">
        {option === "A" && <OptionA/>}
        {option === "B" && <OptionB/>}
        {option === "C" && <OptionC/>}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * OPTION A — One "Agreement" tab, two visually-distinct sections
 * ──────────────────────────────────────────────────────────────────── */
function OptionA() {
  return (
    <div>
      <OptionHeader
        title="Option A — One tab, two sections"
        sub="Merge into a single 'Agreement' tab. Top section is your preferences (one-sided, instant). Bottom section is the signed contract (bilateral, requires signatures)."
        tone="emerald"
      />

      <FakeSellerShell activeTab="Agreement"
        tabs={["Connect Store", "Catalog Sync", "Agreement", "Daily Orders", "Tracking", "Auto-Invoices", "Claims", "Analytics"]}>

        <SectionLabel icon={Settings} label="Your workflow" tone="neutral"
          hint="These are your own preferences. Changes apply instantly — no seller approval needed."/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <MiniCard title="Workflow mode" chip="Daily confirm">
            <div className="text-[12px] text-foreground/65">Review & confirm every order before ship. Change mode at any time.</div>
          </MiniCard>
          <MiniCard title="Per-store schedule" chip="3 stores">
            <MiniRow label="TEST batch shop" tz="ET · New York" cutoff="11:00 AM" invoice="11:30 PM"/>
            <MiniRow label="TEST_shop (ea3cc8)" tz="PT · Los Angeles" cutoff="09:30 AM" invoice="09:00 PM"/>
            <MiniRow label="TEST_shop (3fb5fb)" tz="ET · New York" cutoff="11:00 AM" invoice="11:30 PM"/>
          </MiniCard>
        </div>

        <SectionLabel icon={FileSignature} label="Signed agreement with GlowCo Beauty" tone="indigo"
          hint="Changes require both parties to re-sign. Dual-signature status shown below each clause."/>
        <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/30 p-6 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <Sig who="Buyer (you)" when="Apr 12, 2026"/>
            <div className="text-foreground/30">·</div>
            <Sig who="Seller (GlowCo)" when="Apr 13, 2026"/>
            <span className="ml-auto chip bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ClauseCard title="Responsibilities" rows={[
              ["Late delivery", "Seller"], ["Damaged in transit", "Seller"],
              ["Wrong item shipped", "Seller"], ["No movement 96h+", "Shared"],
            ]}/>
            <ClauseCard title="Shipping SLA" rows={[
              ["Ship within", "48 hours"], ["Tracking push", "Required"], ["Returns", "30 days"],
            ]}/>
            <ClauseCard title="Damage policy" rows={[["Policy", "Replace or refund"]]}/>
            <ClauseCard title="Delay policy"  rows={[["Policy", "Waive shipping after 72h"]]}/>
          </div>
          <div className="mt-4 flex items-center gap-2 text-[12px]">
            <ScrollText className="w-3.5 h-3.5 text-foreground/50"/>
            <span className="text-foreground/60">Contract PDF:</span>
            <a className="underline text-foreground">GlowCo-dropship-v3.pdf</a>
            <span className="text-foreground/40 mono">· 210 KB · uploaded Apr 13</span>
          </div>
        </div>

        <VerdictBox
          pros={["One mental model ('the rules of this relationship')",
                 "Clear visual separation preserves legal weight of signed terms",
                 "Instant-vs-signed distinction stays unambiguous"]}
          cons={["Tab gets long; each section gets less prominence",
                 "Scroll-heavy on small screens"]}
          recommendation="Strong recommendation — cleanest tradeoff, ~45 min to ship."
          tone="emerald"
        />
      </FakeSellerShell>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * OPTION B — Rename Automation → Operations, nest Schedule under Terms
 * ──────────────────────────────────────────────────────────────────── */
function OptionB() {
  return (
    <div>
      <OptionHeader
        title="Option B — 'Operations' + Terms with schedule inside"
        sub="Automation becomes 'Operations' (workflow mode, webhooks). Terms absorbs Schedule alongside responsibilities and SLA."
        tone="amber"
      />

      <FakeSellerShell activeTab="Terms"
        tabs={["Connect Store", "Catalog Sync", "Operations", "Terms", "Daily Orders", "Tracking", "Auto-Invoices", "Claims", "Analytics"]}>
        <div className="display text-2xl mb-1">Terms with GlowCo Beauty</div>
        <div className="text-[13px] text-foreground/60 mb-6">Schedule, responsibilities, SLA, and contract for this trading pair.</div>

        <div className="rounded-2xl border border-border bg-white p-6 space-y-6">
          {/* Schedule — but under Terms */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-foreground/60"/>
              <div className="font-medium">Schedule</div>
              <span className="chip bg-amber-50 text-amber-700 border border-amber-200 text-[9px]">One-sided · buyer preference</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-[12.5px]">
              <MiniField label="Timezone"        value="ET · New York"/>
              <MiniField label="Confirm cutoff"  value="11:00 AM"/>
              <MiniField label="Invoice runs at" value="11:30 PM"/>
            </div>
          </div>

          <Divider/>

          {/* Bilateral terms */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileSignature className="w-4 h-4 text-foreground/60"/>
              <div className="font-medium">Signed terms</div>
              <span className="chip bg-indigo-50 text-indigo-700 border border-indigo-200 text-[9px]">Bilateral · both must sign</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ClauseCard title="Responsibilities" rows={[
                ["Late delivery", "Seller"], ["Damaged", "Seller"], ["Wrong item", "Seller"],
              ]}/>
              <ClauseCard title="SLA & policies" rows={[
                ["Ship within", "48 hours"], ["Returns", "30 days"],
              ]}/>
            </div>
          </div>
        </div>

        <VerdictBox
          pros={["All 'when/how we transact' rules in one place",
                 "Only 1 tab to explain 'the rules'"]}
          cons={["Mixing one-sided preferences under 'Terms' creates cognitive ambiguity (does changing Schedule require the seller to re-sign?)",
                 "Needs a persistent chip/callout to clarify — which adds visual noise",
                 "Seller's 'Operations' still contains workflow mode, which is arguably part of the agreement too"]}
          recommendation="Workable, but the one-sided-vs-bilateral blur is a footgun. Prefer A."
          tone="amber"
        />
      </FakeSellerShell>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * OPTION C — Per-buyer drawer on the Buyers page
 * ──────────────────────────────────────────────────────────────────── */
function OptionC() {
  return (
    <div>
      <OptionHeader
        title="Option C — Per-buyer drawer (drop both tabs)"
        sub="Click a buyer → right drawer with Schedule + Terms + Credit + Contract scoped to that one relationship. Automation + Terms tabs are removed from Dropship."
        tone="indigo"
      />

      {/* Fake Buyers page with drawer overlay */}
      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        {/* Simulated Buyers page */}
        <div className="grid grid-cols-[1fr_480px]">
          <div className="p-6">
            <div className="display text-xl mb-2">Buyers</div>
            <div className="text-[12px] text-foreground/60 mb-4">15 connected · 3 pending · $1.2M GMV YTD</div>
            {[
              { name: "Apex Wholesale LLC",     orders: 847,  status: "Active", highlight: true },
              { name: "Northstar Trading Co.",  orders: 234,  status: "Active" },
              { name: "Brightside Distributors", orders: 112, status: "Active" },
              { name: "Evergreen Retail Group", orders: 56,   status: "Pending" },
            ].map((b, i) => (
              <div key={i} className={`flex items-center gap-4 py-3.5 px-3 rounded-lg border-b border-border/50 last:border-b-0 text-[13px] ${b.highlight ? "bg-foreground/5 ring-1 ring-foreground/20" : ""}`}>
                <Building2 className="w-4 h-4 text-foreground/50"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{b.name}</div>
                  <div className="text-[11px] text-foreground/55">{b.orders} orders · {b.status}</div>
                </div>
                <ChevronRight className={`w-4 h-4 ${b.highlight ? "text-foreground" : "text-foreground/30"}`}/>
              </div>
            ))}
          </div>

          {/* Drawer */}
          <div className="border-l-2 border-foreground/20 bg-secondary/20 p-6 overflow-y-auto max-h-[700px]">
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold">Apex Wholesale LLC</div>
              <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Active</span>
            </div>
            <div className="text-[11px] text-foreground/55 mb-5">apex.com · Net-30 · $45K limit</div>

            <DrawerSection icon={Clock} title="Schedule" chip="One-sided">
              <MiniField label="Timezone" value="ET · New York" small/>
              <MiniField label="Confirm cutoff" value="11:00 AM" small/>
              <MiniField label="Invoice runs at" value="11:30 PM" small/>
            </DrawerSection>

            <DrawerSection icon={FileSignature} title="Signed terms" chip="Active · v3">
              <div className="text-[11.5px] space-y-1.5">
                <div className="flex justify-between"><span className="text-foreground/55">Late delivery</span><span className="font-medium">Seller</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Damaged</span><span className="font-medium">Seller</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Ship within</span><span className="font-medium">48h</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Returns</span><span className="font-medium">30 days</span></div>
              </div>
            </DrawerSection>

            <DrawerSection icon={ScrollText} title="Contract">
              <a className="text-[12px] underline text-foreground">GlowCo-dropship-v3.pdf</a>
              <div className="text-[10.5px] text-foreground/50 mt-1">210 KB · Apr 13 · signed by both</div>
            </DrawerSection>

            <DrawerSection icon={UserCheck} title="Credit & payment">
              <MiniField label="Credit limit" value="$45,000" small/>
              <MiniField label="Utilization" value="$8,217" small/>
              <MiniField label="Terms" value="Net-30" small/>
            </DrawerSection>
          </div>
        </div>
      </div>

      <VerdictBox
        pros={["Matches how sellers actually think — 'I want to update things with Apex'",
               "Reduces nav depth: 2 tabs → 0; all relationship config lives in one drawer",
               "Scales beautifully as the product grows (credit, disputes, payments all fit here)"]}
        cons={["Biggest refactor — ~2 hours",
               "Multi-store buyers need schedule per store inside the drawer (slight UX complexity)",
               "Feels heavy if you have 1000+ buyers — mitigated with search/filter which is already built"]}
        recommendation="Best long-term architecture. Worth it if you plan to add more per-relationship features (credit, disputes, messaging history, SLAs)."
        tone="indigo"
      />
    </div>
  );
}

/* ─────────── Shared mockup primitives ─────────── */

function FakeSellerShell({ tabs, activeTab, children }) {
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden">
      <div className="px-6 pt-5 pb-3 border-b border-border">
        <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">Dropship Automation · seller portal</div>
        <div className="display text-2xl mt-1">GlowCo Beauty's dropship OS</div>
      </div>
      <div className="px-6 border-b border-border flex gap-6 overflow-x-auto">
        {tabs.map(t => (
          <div key={t} className={`py-3 text-[13px] whitespace-nowrap ${t === activeTab ? "border-b-2 border-foreground font-medium" : "text-foreground/50"}`}>
            {t}
          </div>
        ))}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function OptionHeader({ title, sub, tone }) {
  const toneClass = {
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    indigo:  "bg-indigo-50 border-indigo-200",
  }[tone];
  return (
    <div className={`rounded-2xl border p-5 mb-6 ${toneClass}`}>
      <div className="display text-2xl">{title}</div>
      <div className="text-[13.5px] text-foreground/70 mt-1.5 max-w-3xl">{sub}</div>
    </div>
  );
}

function SectionLabel({ icon: Icon, label, tone, hint }) {
  const toneClass = tone === "indigo"
    ? "bg-indigo-50 text-indigo-700 border-indigo-200"
    : "bg-foreground/[0.03] text-foreground border-border";
  return (
    <div className={`rounded-xl px-4 py-3 border mb-3 flex items-start gap-2 ${toneClass}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0"/>
      <div>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em]">{label}</div>
        <div className="text-[12px] text-foreground/65 mt-0.5">{hint}</div>
      </div>
    </div>
  );
}

function MiniCard({ title, chip, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">{title}</div>
        {chip && <span className="ml-auto chip bg-secondary text-[10px]">{chip}</span>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function MiniRow({ label, tz, cutoff, invoice }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center py-1.5 text-[11.5px] border-t border-border/60 first:border-t-0">
      <div className="min-w-0 truncate font-medium">{label}</div>
      <div className="text-foreground/55 mono">{tz}</div>
      <div className="mono">{cutoff}</div>
      <div className="mono">{invoice}</div>
    </div>
  );
}

function ClauseCard({ title, rows }) {
  return (
    <div className="rounded-xl border border-indigo-100 bg-white p-3.5">
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 mb-2">{title}</div>
      <div className="space-y-1.5 text-[12px]">
        {rows.map(([k, v], i) => (
          <div key={i} className="flex justify-between">
            <span className="text-foreground/55">{k}</span>
            <span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sig({ who, when }) {
  return (
    <div className="flex items-center gap-1.5 text-[11.5px]">
      <UserCheck className="w-3.5 h-3.5 text-emerald-600"/>
      <span className="font-medium">{who}</span>
      <span className="text-foreground/50 mono">· {when}</span>
    </div>
  );
}

function MiniField({ label, value, small }) {
  return (
    <div className={small ? "mb-2 last:mb-0" : ""}>
      <div className={`${small ? "text-[9.5px]" : "text-[10px]"} mono uppercase tracking-[0.18em] text-foreground/55`}>{label}</div>
      <div className={`${small ? "text-[12px]" : "text-[13px]"} mono font-medium mt-0.5`}>{value}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-border -mx-6"/>;
}

function DrawerSection({ icon: Icon, title, chip, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-foreground/60"/>
        <div className="text-[12.5px] font-medium">{title}</div>
        {chip && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.15em] text-foreground/50">{chip}</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function VerdictBox({ pros, cons, recommendation, tone }) {
  const toneClass = {
    emerald: "bg-emerald-50/60 border-emerald-200",
    amber:   "bg-amber-50/60 border-amber-200",
    indigo:  "bg-indigo-50/60 border-indigo-200",
  }[tone];
  return (
    <div className={`mt-8 rounded-2xl border p-5 ${toneClass}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-emerald-800 mb-2">PROS</div>
          <ul className="space-y-1.5 text-[12.5px] text-foreground/80">
            {pros.map((p, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600 shrink-0">✓</span>{p}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-rose-800 mb-2">CONS</div>
          <ul className="space-y-1.5 text-[12.5px] text-foreground/80">
            {cons.map((c, i) => <li key={i} className="flex gap-2"><span className="text-rose-500 shrink-0">·</span>{c}</li>)}
          </ul>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-foreground/10 flex items-start gap-2 text-[12.5px]">
        <Info className="w-3.5 h-3.5 mt-0.5 text-foreground/60 shrink-0"/>
        <span><span className="font-semibold">Verdict: </span>{recommendation}</span>
      </div>
    </div>
  );
}
